import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw } from 'lucide-react';
import { api, type IssuedKey, type TenantListItem } from '../lib/api';
import { formatAgo, formatEur, formatInt, providerLabel } from '../lib/format';
import { Badge, Button, Card, ErrorText, Modal, OnlineBadge } from '../components/ui';
import { TenantForm } from '../components/TenantForm';
import { KeyReveal } from '../components/KeyReveal';
import { SuspendToggle } from '../components/SuspendToggle';
import { AiBudgetBadge } from '../components/AiBudgetBadge';

function AiCell({ tenant }: { tenant: TenantListItem }) {
  const hb = tenant.latest;
  if (!hb) return <span className="text-slate-500">—</span>;
  const providers = Object.entries(hb.aiUsage);
  return (
    <div>
      <div className="font-medium text-white">
        {formatEur(hb.aiTotalEur)}
        {hb.aiBudgetEur != null && (
          <span className="text-xs font-normal text-slate-400"> / {formatEur(hb.aiBudgetEur)}</span>
        )}
      </div>
      {providers.length > 0 && (
        <div className="text-xs text-slate-400">
          {providers.map(([p, u]) => `${providerLabel(p)} ${formatEur(u.costEur)}`).join(' · ')}
        </div>
      )}
      <div className="mt-1">
        <AiBudgetBadge usage={tenant.aiBudgetUsage} />
      </div>
    </div>
  );
}

function num(value: number | undefined) {
  return value == null ? <span className="text-slate-500">—</span> : formatInt(value);
}

export function TenantsPage() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: api.tenants,
    refetchInterval: 60_000,
  });

  const list = tenants.data ?? [];
  const up = list.filter((t) => t.online).length;
  const off = list.filter((t) => t.status === 'SUSPENDED').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Clienți Firmio</h1>
        <div className="flex flex-wrap gap-2 text-sm">
          <span title="Instanțe care au trimis heartbeat recent">
            <Badge color="green">{up} online</Badge>
          </span>
          <span title="Instanțe fără heartbeat recent (oprite sau căzute)">
            <Badge color="red">{list.length - up} offline</Badge>
          </span>
          {off > 0 && (
            <span title="Clienți opriți din consolă (OFF): nimeni nu se poate loga">
              <Badge color="amber">{off} cu accesul oprit</Badge>
            </span>
          )}
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={() => void tenants.refetch()}>
            <RefreshCw className={`h-4 w-4 ${tenants.isFetching ? 'animate-spin' : ''}`} />
            Reîncarcă
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Client nou
          </Button>
        </div>
      </div>

      <ErrorText error={tenants.error} />

      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[1000px] text-sm">
          <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-3">Client</th>
              <th className="px-3 py-3 text-center">Status</th>
              <th className="px-3 py-3 text-center">Useri</th>
              <th className="px-3 py-3 text-center">Clienți</th>
              <th className="px-3 py-3 text-center">Furnizori</th>
              <th className="px-3 py-3 text-center">Abonamente</th>
              <th className="px-3 py-3 text-center">Task-uri</th>
              <th className="px-3 py-3 text-center">AI luna aceasta</th>
              <th className="px-3 py-3 text-center">Versiune</th>
              <th className="px-4 py-3 text-center">Acces</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {list.map((t) => (
              <tr key={t.id} className="hover:bg-slate-800/30">
                <td className="px-4 py-3">
                  <Link
                    to={`/tenants/${t.id}`}
                    className="font-medium text-white hover:text-violet-300"
                  >
                    {t.name}
                  </Link>
                  <div className="text-xs text-slate-400">
                    <a
                      href={`https://${t.domain}`}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-slate-200"
                    >
                      {t.domain}
                    </a>
                    {t.overdueInvoices > 0 && (
                      <span className="ml-2">
                        <Badge color="amber">
                          {t.overdueInvoices === 1
                            ? '1 factură restantă'
                            : `${t.overdueInvoices} facturi restante`}
                        </Badge>
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-col items-center gap-1">
                    <OnlineBadge online={t.online} />
                    <span className="text-xs text-slate-500">{formatAgo(t.lastSeenAt)}</span>
                  </div>
                </td>
                <td className="px-3 py-3 text-center">{num(t.latest?.usersActive)}</td>
                <td className="px-3 py-3 text-center">{num(t.latest?.clientsActive)}</td>
                <td className="px-3 py-3 text-center">{num(t.latest?.suppliersActive)}</td>
                <td className="px-3 py-3 text-center">{num(t.latest?.subscriptionsActive)}</td>
                <td className="px-3 py-3 text-center">
                  {num(t.latest?.tasksTotal)}
                  {t.latest && (
                    <div className="text-xs text-slate-500">
                      {formatInt(t.latest.tasksOpen)} deschise
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-center">
                  <AiCell tenant={t} />
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-center text-slate-300">
                  {t.latest?.version ?? '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <SuspendToggle tenantId={t.id} tenantName={t.name} status={t.status} compact />
                  </div>
                </td>
              </tr>
            ))}
            {!tenants.isLoading && list.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-slate-400">
                  Niciun client încă. Adaugă primul cu „Client nou”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {creating && (
        <Modal
          title={issued ? 'Client creat' : 'Client nou'}
          onClose={() => {
            setCreating(false);
            setIssued(null);
          }}
        >
          {issued ? (
            <KeyReveal issued={issued} consoleUrl={window.location.origin} />
          ) : (
            <TenantForm
              submitLabel="Creează"
              onSubmit={async (input) => {
                const res = await api.createTenant(input);
                setIssued({ instanceId: res.instanceId, instanceKey: res.instanceKey });
                await queryClient.invalidateQueries({ queryKey: ['tenants'] });
              }}
            />
          )}
        </Modal>
      )}
    </div>
  );
}
